import type React from 'react';
import { create } from 'zustand';

import { uuid } from '@/lib/uuid';

const MAX_TOASTS = 5;
const DEFAULT_DURATION = 5000;

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface ToastData {
    id: string;
    type: ToastType;
    message: string;
    description?: string;
    duration?: number;
    render?: (props: {
        toast: ToastData;
        onClose: () => void;
    }) => React.ReactNode;
}

interface ToastStoreState {
    toasts: ToastData[];
    add: (toast: Omit<ToastData, 'id'>) => string;
    dismiss: (id: string) => void;
    clear: () => void;
}

export const useToastStore = create<ToastStoreState>((set) => ({
    toasts: [],
    add: (toastInput) => {
        const id = uuid();
        const toast: ToastData = {
            duration: DEFAULT_DURATION,
            ...toastInput,
            id,
        };

        set((state) => {
            const next = [toast, ...state.toasts];
            return { toasts: next.slice(0, MAX_TOASTS) };
        });

        return id;
    },
    dismiss: (id) => {
        set((state) => ({
            toasts: state.toasts.filter((t) => t.id !== id),
        }));
    },
    clear: () => {
        set({ toasts: [] });
    },
}));

export function getToastStore() {
    return useToastStore.getState();
}
